import React, {useCallback, useEffect, useMemo} from 'react';
import {Autocomplete, Box, Chip, ListSubheader, TextField, Typography} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import useAiWidgetStore from '../../../stores/useAiWidgetStore';
import {useSession} from '../../../firebase/UserProvider';
import {useUserFintel} from '../../products/hooks/useUserFintel';
import {useUserDrafts} from '../../products/hooks/useUserDrafts';
import {useUserDisclosures} from '../../products/hooks/useUserDisclosures';

const GLOBAL_OPTION = {id: '__global__', title: 'Global', documentType: 'global', group: ''};

const GROUP_ICON = {
    FINTEL: ArticleOutlinedIcon,
    Disclosures: PolicyOutlinedIcon,
    Drafts: AssignmentOutlinedIcon,
};

function resolveIcon(group) {
    return GROUP_ICON[group] ?? AssignmentOutlinedIcon;
}

const GROUP_ORDER = ['', 'FINTEL', 'Disclosures', 'Drafts'];

/**
 * Thin bar rendered at the top of the GlobalAiAssistantPanel.
 * Supports multi-select of reports within the same type.
 * Selecting reports of a different type is disabled once a type is locked.
 *
 * Props:
 *   suggestedContext — { documentId, documentTitle, documentType } suggested by the
 *                      current page. Applied as initial value if no context is set.
 *   chartContext — { chartId, title } when on `/whiteboards/:id`. Shows a read-only chart
 *                  badge and hides the report document picker (chart grounding replaces it).
 */
const ContextBar = ({suggestedContext, chartContext = null}) => {
    const {user} = useSession();
    const userId = user?.uid;

    const aiContext = useAiWidgetStore((s) => s.aiContext);
    const setAiContext = useAiWidgetStore((s) => s.setAiContext);
    const clearAiContext = useAiWidgetStore((s) => s.clearAiContext);
    const clearThreadId = useAiWidgetStore((s) => s.clearThreadId);
    const addDocumentToContext = useAiWidgetStore((s) => s.addDocumentToContext);

    const {reports: fintelReports, loading: loadingFintel} = useUserFintel(userId);
    const {reports: draftReports, loading: loadingDrafts} = useUserDrafts(userId);
    const {reports: disclosureReports, loading: loadingDisclosures} = useUserDisclosures(userId);

    const loading = loadingFintel || loadingDrafts || loadingDisclosures;

    const options = useMemo(() => {
        // Tag each report with its source bucket so the group label matches the product pages
        // regardless of productType.value (e.g. draft analytics must show under "Drafts", not "FINTEL").
        const taggedReports = [
            ...(fintelReports || []).map((r) => ({r, bucket: 'FINTEL', documentType: r.productType?.value || 'analytic'})),
            ...(draftReports || []).map((r) => ({r, bucket: 'Drafts', documentType: r.productType?.value || 'draft'})),
            ...(disclosureReports || []).map((r) => ({r, bucket: 'Disclosures', documentType: r.productType?.value || 'disclosure'})),
        ];
        const mapped = taggedReports.map(({r, bucket, documentType}) => ({
            id: r.id,
            title: r.title || r.name || r.id,
            documentType,
            group: bucket,
        }));
        mapped.sort((a, b) => {
            const ga = GROUP_ORDER.indexOf(a.group);
            const gb = GROUP_ORDER.indexOf(b.group);
            if (ga !== gb) return ga - gb;
            return a.title.localeCompare(b.title);
        });
        return [GLOBAL_OPTION, ...mapped];
    }, [fintelReports, draftReports, disclosureReports]);

    // The array of currently selected option objects (excludes GLOBAL_OPTION)
    const selectedValues = useMemo(() => {
        if (!aiContext?.documents?.length) return [];
        return aiContext.documents
            .map((d) => options.find((o) => o.id === d.documentId))
            .filter(Boolean);
    }, [aiContext, options]);

    const handleChange = useCallback((_e, newValues) => {
        // If the user cleared everything or selected Global
        if (!newValues || newValues.length === 0 ||
            (newValues.length === 1 && newValues[0].id === '__global__')) {
            clearAiContext();
            clearThreadId();
            return;
        }

        // Filter out the Global sentinel if it appears mixed in
        const filtered = newValues.filter((v) => v.id !== '__global__');

        // Lock to the first selected item's group bucket; only keep same-bucket items
        const firstGroup = filtered[0]?.group;
        const firstType = filtered[0]?.documentType;
        const sameGroup = filtered.filter((v) => v.group === firstGroup);

        setAiContext({
            documentType: firstType,
            documents: sameGroup.map((v) => ({documentId: v.id, documentTitle: v.title})),
        });
        clearThreadId();
    }, [setAiContext, clearAiContext, clearThreadId]);

    // Apply suggestedContext when reports have loaded and the report is in the list.
    // - If no context is set yet: set it fresh (initial behaviour).
    // - If context is already set: enrich it with the new document WITHOUT clearing
    //   the active thread, so the conversation continues with both documents.
    useEffect(() => {
        if (!suggestedContext || loading) return;
        if (!options.some((o) => o.id === suggestedContext.documentId)) return;

        if (!aiContext) {
            setAiContext({
                documentType: suggestedContext.documentType,
                documents: [{
                    documentId: suggestedContext.documentId,
                    documentTitle: suggestedContext.documentTitle,
                }],
            });
        } else {
            // addDocumentToContext is a no-op when the document is already present
            // or when the document type differs from the locked type.
            addDocumentToContext({
                documentId: suggestedContext.documentId,
                documentTitle: suggestedContext.documentTitle,
                documentType: suggestedContext.documentType,
            });
        }
    }, [suggestedContext, aiContext, loading, options, setAiContext, addDocumentToContext]);

    const isGlobal = !aiContext || selectedValues.length === 0;
    const lockedGroup = selectedValues[0]?.group || null;
    const hasChartContext = Boolean(chartContext?.chartId);

    return (
        <Box sx={{
            px: 1,
            pt: 0.5,
            pb: 0.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25}}>
                <Typography variant="caption" color="text.secondary"
                            sx={{fontWeight: 500, fontSize: 10, flex: 1}}>
                    Context
                </Typography>
                {hasChartContext ? (
                    <Chip
                        size="small"
                        label="Chart"
                        sx={{height: 16, fontSize: 10, fontWeight: 600}}
                        color="primary"
                        variant="outlined"
                    />
                ) : null}
                {!hasChartContext && !isGlobal && lockedGroup && (
                    <Chip
                        size="small"
                        label={`${lockedGroup} · ${selectedValues.length}`}
                        sx={{height: 16, fontSize: 10, fontWeight: 600}}
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Box>
            {hasChartContext ? (
                <Box
                    data-testid="chart-context-badge"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 0.5,
                        px: 0.5,
                        py: 0.5,
                    }}
                >
                    <AccountTreeOutlinedIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
                    <Typography variant="body2" noWrap sx={{fontSize: 12, fontWeight: 500, flex: 1}}>
                        {chartContext.title || chartContext.chartId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: 10}}>
                        Read-only
                    </Typography>
                </Box>
            ) : (
                <Autocomplete
                    multiple
                    size="small"
                    options={options}
                    value={selectedValues}
                    onChange={handleChange}
                    loading={loading}
                    disableCloseOnSelect
                    groupBy={(o) => o.group}
                    getOptionLabel={(o) => o.title || ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    getOptionDisabled={(option) => {
                        // Global option is never disabled
                        if (option.id === '__global__') return false;
                        // Lock to selected group bucket once at least one report is chosen
                        return lockedGroup !== null && option.group !== lockedGroup;
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={isGlobal ? 'Global (no context)' : undefined}
                            variant="outlined"
                            size="small"
                            sx={{'& .MuiOutlinedInput-root': {fontSize: 12, py: '2px !important'}}}
                        />
                    )}
                    renderTags={(tagValues, getTagProps) =>
                        tagValues.map((option, index) => {
                            const {key, ...tagProps} = getTagProps({index});
                            const Icon = resolveIcon(option.group);
                            return (
                                <Chip
                                    key={key}
                                    {...tagProps}
                                    size="small"
                                    icon={<Icon style={{fontSize: 11}}/>}
                                    label={option.title}
                                    sx={{height: 18, fontSize: 10, maxWidth: 140}}
                                />
                            );
                        })
                    }
                    renderGroup={(params) => (
                        <Box key={params.key}>
                            {params.group && (
                                <ListSubheader
                                    component="div"
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        lineHeight: '22px',
                                        color: 'text.secondary',
                                        bgcolor: 'background.default',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        px: 1.5,
                                    }}
                                >
                                    {params.group}
                                </ListSubheader>
                            )}
                            {params.children}
                        </Box>
                    )}
                    renderOption={(props, option, {selected}) => {
                        const Icon = option.id === '__global__'
                            ? PublicIcon
                            : resolveIcon(option.group);
                        const isDisabled = lockedGroup !== null &&
                            option.id !== '__global__' &&
                            option.group !== lockedGroup;
                        return (
                            <Box
                                component="li"
                                {...props}
                                key={option.id}
                                sx={{
                                    gap: 1,
                                    pl: option.group ? 2 : 1.5,
                                    fontSize: 12,
                                    opacity: isDisabled ? 0.4 : 1,
                                }}
                            >
                                <Icon sx={{fontSize: 13, color: selected ? 'primary.main' : 'text.secondary', flexShrink: 0}}/>
                                <Typography variant="body2" noWrap sx={{fontSize: 12}}>
                                    {option.title}
                                </Typography>
                            </Box>
                        );
                    }}
                    sx={{mb: 0.25}}
                />
            )}
        </Box>
    );
};

export default ContextBar;
